:host {
  --font-family: "Visa Dialect", sans-serif;
  display: block;
  font-family: var(--font-family);
  box-sizing: border-box;
}

.vsr-card-theme-light {
  --color-card-bg: #f7f7f7;
  --color-title-text: #1a1a1a;
  --color-title-hover: #1434cb;
  --color-description-text: #656565;
}

.vsr-card-theme-dark {
  --color-card-bg: #2c2c2c;
  --color-title-text: #ffffff;
  --color-title-hover: #8f9fe4;
  --color-description-text: #ffffff;
}

.vsr-card {
  display: flex;
  flex-direction: column;
  border: none;
  box-shadow: none;
  width: auto;
  box-sizing: border-box;
  max-width: 300px;
  @media (max-width: 480px) {
    max-width: none;
  }
}

.vsr-card__image-container {
  width: 300px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f6f6f6;
  overflow: hidden;
  @media (max-width: 480px) {
    width: auto;
  }
}

.vsr-card__image-container.b-radius {
  border-radius: 2%;
}

.vsr-card__image {
  position: relative;
  overflow: hidden;
  width: 100%;
  object-fit: cover;
  object-position: center;
  max-width: 300px;
  display: block;
  transition: transform 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  &::after {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(255, 255, 255, 0.735);
    opacity: 0;
    pointer-events: none;
    transition: opacity 0.4s;
  }
  @media (max-width: 480px) {
    max-width: none;
  }
}

/* Aspect ratio classes */
.vsr-card__image-container.aspect-ratio-3-4 .vsr-card__image {
  aspect-ratio: 3 / 4;
  height: auto;
  animation-fill-mode: none;
}

.vsr-card__image-container.aspect-ratio-4-3 .vsr-card__image {
  aspect-ratio: 4 / 3;
  height: auto;
  animation-fill-mode: none;
}

.vsr-card__image-container.aspect-ratio-4-5 .vsr-card__image {
  aspect-ratio: 4 / 5;
  height: auto;
  animation-fill-mode: none;
}

.vsr-card__image-container.aspect-ratio-16-9 .vsr-card__image {
  aspect-ratio: 16 / 9;
  height: auto;
  animation-fill-mode: none;
}

.vsr-card__image-container.aspect-ratio-1-1 .vsr-card__image {
  aspect-ratio: 1 / 1;
  height: auto;
  animation-fill-mode: none;
}

.vsr-card__content {
  margin-top: 16px;
}

.vsr-card__label,
.vsr-card__title,
.vsr-card__description {
  cursor: default;
  word-break: break-word;
  overflow-wrap: break-word;
  white-space: normal;
  width: 100%;
  box-sizing: border-box;
  text-decoration: none;
  p {
    margin: 0;
  }
}

.vsr-card__label {
  font-size: 0.75rem;
  font-weight: 500;
  line-height: 1.3;
  color: #767676;
  margin: 0 0 0.5rem;
}

.vsr-card__title {
  font-size: 1.25rem;
  font-weight: 400;
  line-height: 1.3;
  color: var(--color-title-text);
  margin: 0 0 0.5rem;

  // Focus styles for non-linked titles
  &:focus-visible {
    outline: 2px dashed #005fcc;
    outline-offset: 2px;
    border-radius: 4px;
    color: #005fcc; // Change text color to blue on focus
  }

  // Focus styles for linked titles
  a {
    text-decoration: none;
    color: inherit;

    &:focus-visible {
      outline: 2px dashed #005fcc;
      outline-offset: 2px;
      border-radius: 4px;
      color: #005fcc; // Change text color to blue on focus
    }

    &:hover {
      color: #005fcc; // Change text color to blue on focus
      text-decoration: none;
    }
  }
}

.vsr-card__title a {
  text-decoration: none;
  color: inherit;
  outline: none;
}

.vsr-card__title a:hover {
  text-decoration: none;
  color: var(--color-title-hover);
}

.vsr-card__title a:focus {
  color: var(--color-title-hover);
  outline: 2px dashed var(--color-title-hover);
  outline-offset: 2px;
}

.vsr-card__title a:visited,
.vsr-card__title a:active {
  text-decoration: none;
  color: inherit;
}

.vsr-card__description {
  font-size: 0.875rem;
  font-weight: 400;
  line-height: 1.5;
  color: var(--color-description-text);
  margin: 0 0 0.5rem;
}

.vsr-card__actions-row {
  display: flex;
  gap: 16px;
  margin-top: 0.75em;
  @media (max-width: 325px) {
    flex-direction: column;
  }
}

.vsr-card__action-button {
  font-size: 0.875rem;
  color: var(--color-title-text);
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
  gap: 2px;
  font-weight: 400;
  line-height: 1.4;
  transition: color 0.2s;
  font-family: "Visa Dialect", sans-serif;
  text-decoration: none;
  &:hover {
    color: var(--color-title-hover);
    text-decoration: none;
  }
}

.vsr-card__action-button svg {
  margin-left: 6px;
  display: inline-block;
  vertical-align: middle;
}

.vsr-card__fixed-image {
  height: 300px;
  background-color: var(--color-card-bg);
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  text-decoration: none;
  &:hover {
    text-decoration: none;
  }
}

@media (max-width: 900px) {
  .vsr-card__fixed-image {
    width: auto;
  }
}

.vsr-card__fixed-image img {
  width: 170px;
  height: 170px;
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  transition: transform 0.2s cubic-bezier(0.4, 0, 0.2, 1) !important;
}

@keyframes flash-in {
  0% {
    opacity: 0.5;
  }
}

.vsr-card:not(.no-hover-16-9):hover .vsr-card__fixed-image img {
  transform: scale(1.06);
}

.vsr-card.no-hover-16-9:hover .vsr-card__fixed-image img {
  transform: none;
}

.vsr-card:hover .vsr-card__image.zoom.has-hover-effect {
  transform: scale(1.05);
}

.vsr-card:hover .vsr-card__image.pulsate.has-hover-effect {
  transform: scale(1.05);
  animation: flash-in 0.4s;
}

.vsr-card:hover .vsr-card__image.no-hover-effect,
.vsr-card.no-hover-16-9:hover .vsr-card__image {
  transform: none;
  animation: none;
}

/* Remove hover effect on title for 16:9 aspect ratio */
.vsr-card.no-hover-16-9.has-card-link:hover .vsr-card__title {
  color: var(--color-title-text);
}

.vsr-card.no-hover-16-9 .vsr-card__title a:hover {
  color: inherit;
}

.vsr-card.has-card-link:hover .vsr-card__title {
  color: var(--color-title-hover);
}

.vsr-card__action-button:hover {
  text-decoration: none;
  color: var(--color-title-hover);
}

.vsr-card__action-button:hover svg {
  transition:
    width 0.2s ease-in-out,
    height 0.2s ease-in-out;
  margin-left: 8px;
}

/* Support for 2up-first and 2up-second image variants */
.vsr-card__image-container[class*="2up-first"] {
  width: 100%;
  max-width: 648px;
  background: transparent;
}

.vsr-card__image-container[class*="2up-first"] .vsr-card__image {
  max-width: 648px;
}

.vsr-card__image-container[class*="2up-second"] {
  width: 100%;
  max-width: 366px;
  background: transparent;
}

.vsr-card__image-container[class*="2up-second"] .vsr-card__image {
  max-width: 366px;
  max-height: 465px;
}
